import { FC, useEffect, useMemo, useState } from "react";

import { useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";

import { PointIcon, RewardIcon } from "../../../../../../assets/svg";
import { PyButton } from "../../../../../../components";
import { CardFrame } from "../../../../../../components/CardFrame/CardFrame";
import { CHECKOUT_SESSION_KEY, usePayLinkCart } from "../../../../context/PayLinkCartContext";
import { usePayLinkUser } from "../../../../context/PaylinkUserContext";
import { useLoyaltyEnrolment } from "../../../../hook";
import { getAccuredPoint, getAdminDetails, getEarnableRewards } from "../../../../services/api";
import { TShiping } from "../../../../types/walllet";
import { convertFromNairaToKobo, formatCart } from "../../../../../../utils/helper";
import { LoyaltyEnrollmentModal } from "./LoyaltyEnrollmentModal";
import { PointCard } from "./PointCard";
import { RewardCard } from "./RewardCard";

interface IProps {
  handleNextView: () => void;
  totalAmount: number;
  isEnrollmentDone: boolean;
  setIsEnrollmentDone: React.Dispatch<React.SetStateAction<boolean>>;
  isDisabled?: boolean;
}

export const Rewards: FC<IProps> = ({
  handleNextView,
  totalAmount,
  isEnrollmentDone,
  setIsEnrollmentDone,
  isDisabled = false,
}) => {
  const { accounts } = useMsal();
  const { storeProfile, sessionUniqueReferenceNumber, cartItems } = usePayLinkCart();
  const {
    user,
    reward: selectedReward,
    freeShippingReward,
    setReward: setSelectedReward,
    setFreeShippingReward,
    setPointMerchant,
    setRedeemedPoints,
    adminDetails,
    shippingRate,
  } = usePayLinkUser();

  const [pointCheck, setPointCheck] = useState<boolean>(false);
  const [rewardCheck, setRewardCheck] = useState<boolean>(false);

  // Auto-check the rewards section when a reward is selected
  useEffect(() => {
    if (selectedReward || freeShippingReward) {
      setRewardCheck(true);
    }
  }, [selectedReward, freeShippingReward]);

  const isAuthenticated = !!accounts && accounts.length >= 1;
  const {
    isEnrolmentLoading,
    operationState,
    handleEnrollment,
    handleProceedToCheckout,
    isEnrollingForLoyalty,
    firstCartItem,
  } = useLoyaltyEnrolment({
    setIsEnrollmentDone,
    enabled: isAuthenticated,
  });

  const { data: pointsData } = useQuery({
    queryKey: ["GetAccuredPoint"],
    queryFn: () =>
      getAccuredPoint({
        CustomerUniqueReferenceNumber:
          storeProfile?.customerUniqueReferenceNumber as string,
        ConsumerUniqueReferenceNumber:
          user?.consumerUniqueReferenceNumber as string,
      }),
    enabled:
      !!user?.consumerUniqueReferenceNumber &&
      !!storeProfile?.customerUniqueReferenceNumber,
    retry: 0,
    staleTime: Infinity,
  });

  const hasPoints = useMemo(() => {
    const userPoints = pointsData?.data ?? [];
    return userPoints.some(
      (item) => (item.earnedBalance || 0) - (item.spentBalance || 0) > 0,
    );
  }, [pointsData]);

  const { data: localAdminData } = useQuery({
    queryKey: ["PayLinkRewardAdminDetails"],
    queryFn: () => getAdminDetails({ accountType: "Admin" }),
    retry: 0,
    staleTime: Infinity,
  });

  const resolvedAdminUrn =
    adminDetails?.uniqueReferenceNumber ||
    localAdminData?.data?.[0]?.uniqueReferenceNumber ||
    "";

  const effectiveSessionUrn =
    sessionUniqueReferenceNumber ||
    localStorage.getItem(CHECKOUT_SESSION_KEY) ||
    "";

  const canFetchRewards =
    !!user?.consumerUniqueReferenceNumber &&
    !!user?.walletUniqueReferenceNumber &&
    !!storeProfile?.merchantUniqueReferenceNumber &&
    !!storeProfile?.customerUniqueReferenceNumber &&
    !!effectiveSessionUrn;

  const formattedCart = useMemo(
    () =>
      formatCart(
        (cartItems || []).map((item: any) => ({
          ...item,
          image:
            item?.image || item?.productImages?.[0]?.documentAbsoluteUri || "",
          price: convertFromNairaToKobo(Number(item?.price || 0)),
        })),
      ),
    [cartItems],
  );

  const { data: rewardsData } = useQuery({
    queryKey: ["PayLinkApplicableRewards", effectiveSessionUrn],
    queryFn: () =>
      getEarnableRewards({
        adminUniqueReferenceNumber:
          resolvedAdminUrn || storeProfile?.merchantUniqueReferenceNumber || "",
        consumerUniqueReferenceNumber:
          user?.consumerUniqueReferenceNumber as string,
        walletUniqueReferenceNumber:
          user?.walletUniqueReferenceNumber as string,
        merchantUniqueReferenceNumber:
          storeProfile?.merchantUniqueReferenceNumber as string,
        customerUniqueReferenceNumber:
          storeProfile?.customerUniqueReferenceNumber as string,
        uniqueReferenceNumber: effectiveSessionUrn,
        cart: formattedCart,
        shipping: shippingRate as TShiping,
      }),
    enabled: canFetchRewards,
    retry: 0,
    staleTime: Infinity,
  });

  const hasRewards = useMemo(() => {
    const sessionApplicable = Array.isArray(
      rewardsData?.data?.sessionRewardsApplicable,
    )
      ? rewardsData?.data?.sessionRewardsApplicable
      : [];
    return sessionApplicable.some(
      (reward: any) =>
        !!reward?.uniqueReferenceNumber ||
        !!reward?.rewardUniqueReferenceNumber ||
        !!reward?.promotionUniqueReferenceNumber ||
        !!reward?.loyaltyPromotion?.promotionUniqueReferenceNumber,
    );
  }, [rewardsData]);

  const hasLoyaltyPromotion = !!firstCartItem?.loyaltyPromotion;
  const shouldShowEnrollmentModal =
    hasLoyaltyPromotion &&
    !isEnrollmentDone &&
    (isEnrolmentLoading ||
      operationState === "AlreadyEnrolled" ||
      operationState === "EnrolledForLoyalty" ||
      operationState === "FailedLoyalty");

  const handlePointCheck = () => {
    if (pointCheck) {
      setPointCheck(false);
      setPointMerchant(null);
      setRedeemedPoints(0);
    } else {
      setPointCheck(true);
    }
  };

  const handleRewardCheck = () => {
    if (rewardCheck) {
      setRewardCheck(false);
      setSelectedReward(null);
      setFreeShippingReward(null);
    } else {
      setRewardCheck(true);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <LoyaltyEnrollmentModal
        open={shouldShowEnrollmentModal}
        operationState={operationState}
        merchantName={storeProfile?.storeName}
        isRetryLoading={isEnrollingForLoyalty}
        onRetry={handleEnrollment}
        onProceed={handleProceedToCheckout}
      />

      {isEnrolmentLoading && (
        <p className="text-xs lg:text-sm text-py_black_600">
          Enrolling you for loyalty in the background...
        </p>
      )}

      {/* Point section */}
      <div>
        <CardFrame
          isFlat
          hasIcon={true}
          icon={<PointIcon />}
          title={"Points"}
          handleCheck={handlePointCheck}
          checkState={pointCheck}
          disabled={!hasPoints}>
          <PointCard isPointChecked={pointCheck} totalAmount={totalAmount} />
        </CardFrame>
      </div>

      {/* Reward section */}
      <div>
        <CardFrame
          isFlat
          hasIcon={true}
          icon={<RewardIcon />}
          title={"Rewards"}
          handleCheck={handleRewardCheck}
          checkState={rewardCheck}
          disabled={!hasRewards}>
          <RewardCard isEnrolmentLoading={isEnrolmentLoading} />
        </CardFrame>
      </div>

      <PyButton
        click={handleNextView}
        isDisabled={isDisabled}
        extraClass="!w-full lg:!w-fit !mt-4">
        {hasPoints || hasRewards ? "Apply rewards" : "Proceed to checkout"}
      </PyButton>
    </section>
  );
};
